import { useState, useCallback, useRef } from 'react';
import {
  formatDateForSQLQuery,
  getCurrentTimezoneString,
} from '../../utils/dateTimeUtils';

const PAGE_SIZE = 25;
const DEFAULT_RETENTION_DAYS = 60;

/**
 * Self-contained hook for fetching test history from the insights API.
 * No Redux dependency — manages state internally.
 *
 * @param {Object} options
 * @param {Object} options.testDetail  - Test detail (id, name, type, mode, feature_flag)
 * @param {string} options.apiBaseUrl  - Base URL for the insights API (e.g. from REACT_APP_API_BASE_URL_PREFIX)
 * @param {string} options.authToken   - Bearer auth token
 */
export function useTestHistory({ testDetail, apiBaseUrl, authToken, index = 'testInfoAutomationHE' }) {
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSilentLoading, setIsSilentLoading] = useState(false);
  const [error, setError] = useState(null);
  const recordIndexRef = useRef(0);

  const fetchHistory = useCallback(
    async (fromIndex = 0, silent = false) => {
      if (!testDetail || !apiBaseUrl || !authToken) return;

      const {
        name: testName = '',
        type: testType = '',
        feature_flag: featureFlags = {},
        mode = '',
        id: testId = '',
      } = testDetail;

      const retentionDays =
        featureFlags?.testArtefactCloudStorageConfig?.retentionDays ||
        featureFlags?.retention_days ||
        DEFAULT_RETENTION_DAYS;

      let testTypeToPass = testType;
      if (mode === 'hypertest' && !testType?.includes?.('hypertest')) {
        testTypeToPass = `hypertest-${testType}`;
      }

      const sanitizedTestName = (testName || '').replace(/"/g, '\\"').replace(/'/g, "\\'");
      const testNameQuery = sanitizedTestName
        ? `test_name IN ('${sanitizedTestName}')`
        : `test_id IN ('${testId}')`;

      const currentDate = new Date();
      const prevDate = new Date();
      prevDate.setDate(currentDate.getDate() - Number(retentionDays));
      const timezone = getCurrentTimezoneString();

      const timeStampQuery = `create_timestamp BETWEEN '${formatDateForSQLQuery(
        prevDate,
      )}' AND '${formatDateForSQLQuery(
        currentDate,
      )}' AND timezone = '${timezone}'`;

      const payload = {
        query: {
          size: PAGE_SIZE,
          from: fromIndex,
          sort: [{ create_timestamp: { order: 'desc' } }],
          _source: [
            'create_timestamp',
            'test_id',
            'status',
            'browser',
            'browser_version',
            'device',
            'os',
            'os_version',
            'resolution',
            'duration',
            'flake_rate',
            'username',
          ],
          aggs: {
            total_tests: { value_count: { field: 'test_id.keyword' } },
          },
        },
        meta: { isEncoded: true, type: 'list' },
        index,
        filters: btoa(
          `${testNameQuery} AND test_type IN ('${testTypeToPass}') AND ${timeStampQuery} AND is_deleted IS false`,
        ),
      };

      if (silent) {
        setIsSilentLoading(true);
      } else {
        setIsLoading(true);
      }

      try {
        const res = await fetch(`${apiBaseUrl}/insights/api/v3/data`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!res.ok) throw new Error(`API error: ${res.status}`);

        const result = await res.json();
        const newRows = result?.data || [];
        const newMeta = result?.meta || {};

        setData((prev) => (fromIndex === 0 ? newRows : [...prev, ...newRows]));
        setMeta(newMeta);
        recordIndexRef.current = fromIndex + PAGE_SIZE;
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
        setIsSilentLoading(false);
      }
    },
    [testDetail, apiBaseUrl, authToken, index],
  );

  const loadMore = useCallback(() => {
    fetchHistory(recordIndexRef.current, true);
  }, [fetchHistory]);

  const totalCount = meta?.total_tests || 0;
  const hasMore = data.length < totalCount;

  const reset = useCallback(() => {
    setData([]);
    setMeta({});
    recordIndexRef.current = 0;
    setError(null);
    setIsLoading(false);
    setIsSilentLoading(false);
  }, []);

  return {
    data,
    isLoading,
    isSilentLoading,
    error,
    hasMore,
    totalCount,
    fetchHistory,
    loadMore,
    reset,
  };
}
